/**
 * MissingDataStep Component
 * Step 3: Handle cases where data might be missing
 */

import React from 'react'
import { motion } from 'framer-motion'
import { EyeOff, Shield, GitBranch, ArrowLeft } from 'lucide-react'
import { MissingDataIntent } from '../../../types/macros'

interface MissingDataStepProps {
  dataKey: string
  missingDataIntent: MissingDataIntent
  fallbackValue: string
  conditionalContent: string
  elseContent: string
  onIntentChange: (intent: MissingDataIntent) => void
  onFallbackChange: (value: string) => void
  onConditionalContentChange: (content: string) => void
  onElseContentChange: (content: string) => void
  onBack: () => void
}

const MissingDataStep: React.FC<MissingDataStepProps> = ({
  dataKey,
  missingDataIntent,
  fallbackValue,
  conditionalContent,
  elseContent,
  onIntentChange,
  onFallbackChange,
  onConditionalContentChange,
  onElseContentChange,
  onBack
}) => {
  const options = [
    {
      value: 'none' as MissingDataIntent,
      icon: EyeOff,
      label: 'Show Nothing',
      description: 'Leave blank if data doesn\'t exist',
      pattern: `{{${dataKey}}}`,
      example: {
        withData: `Hello {{${dataKey}}}! → Hello John!`,
        withoutData: `Hello {{${dataKey}}}! → Hello !`
      },
      color: 'blue'
    },
    {
      value: 'fallback' as MissingDataIntent,
      icon: Shield,
      label: 'Use Fallback Text',
      description: 'Show default text when data is missing',
      pattern: `{{${dataKey}|'fallback'}}`,
      example: {
        withData: `Hello {{${dataKey}}}! → Hello John!`,
        withoutData: `Hello {{${dataKey}|'Guest'}}! → Hello Guest!`
      },
      color: 'green',
      hasInput: true
    },
    {
      value: 'conditional' as MissingDataIntent,
      icon: GitBranch,
      label: 'Show Different Message',
      description: 'Display different content based on data presence',
      pattern: `{{#if ${dataKey}}}...{{else}}...{{/if}}`,
      example: {
        withData: `{{#if ${dataKey}}}Welcome back, {{${dataKey}}}!{{else}}Welcome!{{/if}} → Welcome back, John!`,
        withoutData: `{{#if ${dataKey}}}Welcome back, {{${dataKey}}}!{{else}}Welcome!{{/if}} → Welcome!`
      },
      color: 'purple',
      hasInput: true
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-[#6E6D7A] hover:text-[#1A2B49] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to data key
        </button>
        
        <h2 className="text-2xl font-bold text-[#1A2B49] mb-2">
          What if <code className="text-[#FE4215] bg-[#FE4215]/10 px-2 py-1 rounded">{dataKey}</code> is missing?
        </h2>
        <p className="text-[#6E6D7A]">
          Choose how to handle cases where this data might not be available.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4">
        {options.map((option) => {
          const Icon = option.icon
          const isSelected = missingDataIntent === option.value
          
          type ColorClasses = {
            border: string
            bg: string
            icon: string
            text: string
          }
          
          const colorConfig: Record<string, ColorClasses> = {
            blue: {
              border: 'border-blue-500',
              bg: 'bg-blue-50',
              icon: 'bg-blue-500',
              text: 'text-blue-700'
            },
            green: {
              border: 'border-green-500',
              bg: 'bg-green-50',
              icon: 'bg-green-500',
              text: 'text-green-700'
            },
            purple: {
              border: 'border-purple-500',
              bg: 'bg-purple-50',
              icon: 'bg-purple-500',
              text: 'text-purple-700'
            }
          }
          
          const colorClasses: ColorClasses = colorConfig[option.color] || colorConfig.blue

          return (
            <div key={option.value}>
              <motion.button
                onClick={() => onIntentChange(option.value)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? `${colorClasses.border} ${colorClasses.bg} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 p-3 rounded-lg ${
                      isSelected ? `${colorClasses.icon} text-white` : 'bg-gray-100 text-[#6E6D7A]'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#1A2B49] mb-1">
                      {option.label}
                    </h3>
                    <p className="text-sm text-[#6E6D7A] mb-3">
                      {option.description}
                    </p>

                    {/* Pattern Preview */}
                    <div className="mb-3 p-2 bg-gray-900 rounded">
                      <code className="text-xs text-green-400 font-mono">
                        {option.pattern}
                      </code>
                    </div>

                    {/* Examples */}
                    <div className="space-y-1.5 text-xs">
                      <div className={`p-2 rounded ${isSelected ? 'bg-white' : 'bg-gray-50'}`}>
                        <span className="text-[#6E6D7A]">With data:</span>
                        <code className="block mt-1 text-[#233D71] font-mono">
                          {option.example.withData}
                        </code>
                      </div>
                      <div className={`p-2 rounded ${isSelected ? 'bg-white' : 'bg-gray-50'}`}>
                        <span className="text-[#6E6D7A]">Without data:</span>
                        <code className="block mt-1 text-[#233D71] font-mono">
                          {option.example.withoutData}
                        </code>
                      </div>
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className={`flex-shrink-0 w-6 h-6 ${colorClasses.icon} rounded-full flex items-center justify-center`}
                    >
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </motion.div>
                  )}
                </div>
              </motion.button>

              {/* Additional Inputs for Selected Option */}
              {isSelected && option.hasInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4"
                >
                  {option.value === 'fallback' && (
                    <div>
                      <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                        Fallback Text
                      </label>
                      <input
                        type="text"
                        value={fallbackValue}
                        onChange={(e) => onFallbackChange(e.target.value)}
                        placeholder="e.g., Guest, Valued Customer, friend"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="text-xs text-[#6E6D7A] mt-2">
                        This text will be shown when <code className="bg-gray-200 px-1 rounded">{dataKey}</code> has no value
                      </p>
                    </div>
                  )}

                  {option.value === 'conditional' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                          Message When Data Exists
                        </label>
                        <textarea
                          value={conditionalContent}
                          onChange={(e) => onConditionalContentChange(e.target.value)}
                          placeholder={`e.g., Hey {{${dataKey}}}, your order is ready!`}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                        />
                        <p className="text-xs text-[#6E6D7A] mt-2">
                          💡 You can use <code className="bg-gray-200 px-1 rounded">{`{{${dataKey}}}`}</code> or <code className="bg-gray-200 px-1 rounded">{`{{${dataKey}|'fallback'}}`}</code> here
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                          Message When Data Doesn't Exist (Optional)
                        </label>
                        <textarea
                          value={elseContent}
                          onChange={(e) => onElseContentChange(e.target.value)}
                          placeholder="e.g., Hello, your order is ready!"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                        />
                        <p className="text-xs text-[#6E6D7A] mt-2">
                          Leave empty if you don't want to show anything when data is missing
                        </p>
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MissingDataStep
